<script setup>
import { ref, reactive, watch, defineProps, defineEmits, onMounted } from "vue";
import AppIcon from "./AppIcon.vue";
import { apiQuery } from "@/api/j24api";
import AlertsList from "@/components/AlertsList.vue";

const props = defineProps({
  selectedLicense: Object,
  appsList: Array,
  devices: Array,
  loading: Boolean,
  addCommand: Boolean,
  selectedApp: Object,
  existingCommands: Array,
  hideTitle: Boolean,
});

const emit = defineEmits(["saved-new-command", "cancel-new-command"]);

const addNewCommand = reactive({ value: {} });
const addNewCommandSaveDisabled = ref(true);
const commandSearchDevice = ref("");
const alerts = ref([]);
const appSearch = ref("");
const loading = ref(false);

onMounted(() => {
  if (props.selectedApp) {
    console.log("Selected App:", props.selectedApp);
    let appsList = cleanAppsList();
    let match = appsList.find((a) => a.appId === props.selectedApp.id);
    if (match) {
      addNewCommand.value = { ...match };
    } else {
      console.log("No match found for selected app in apps list");
    }
  }
});

function selectApp(x, row) {
  addNewCommand.value = row.item;
}

function addNewSelectDevice(x, row) {
  addNewCommand.value.serial = row.item.serial;
}

const addAppHeaders = [
  { title: "Application ID", value: "appId" },
  { title: "Name", value: "name" },
  { title: "Version", value: "version" },
];

const chooseDeviceHeaders = [
  { title: "Serial Number", value: "serial" },
  { title: "Last Contact", value: "lastContact" },
  { title: "Device Name", value: "computerName" },
  { title: "Username", value: "username" },
  { title: "Full Name", value: "fullName" },
];

const installTypes = [
  { title: "Install Soon", value: "installSoon" },
  { title: "Install ASAP", value: "installASAP" },
];

watch(addNewCommand, (newValue, oldValue) => {
  if (!addNewCommand.value.scope) {
    addNewCommandSaveDisabled.value = true;
    return;
  }
  if (!addNewCommand.value.type) {
    addNewCommandSaveDisabled.value = true;
    return;
  }
  switch (addNewCommand.value.scope) {
    case "configuration":
      if (!addNewCommand.value.groupID) {
        addNewCommandSaveDisabled.value = true;
        return;
      }
      break;
    case "device":
      if (!addNewCommand.value.serial) {
        addNewCommandSaveDisabled.value = true;
        return;
      }
      break;
  }
  addNewCommandSaveDisabled.value = false;
});

function cleanAppsList() {
  let result = [];
  props.appsList.forEach((app) => {
    let match = result.find((a) => a.appId === app.id);
    if (!match) {
      if (!app.latestApprovedPatch) {
        return;
      }
      result.push({
        appId: app.id,
        name: app.name,
        version: app.latestApprovedPatch?.version,
      });
      return;
    }
    try {
      if (app.latestApprovedPatch.version != match.version) {
        result.push({
          appId: app.id,
          name: app.name,
          version: app.latestApprovedPatch.version,
        });
      }
    } catch (error) {
      console.log("Error processing app for command list", app, error);
    }
  });
  return result;
}

async function saveNewCommand() {
  loading.value = true;
  try {
    let action = "addDeviceCommand";
    if (
      addNewCommand.value.scope == "configuration" ||
      addNewCommand.value.scope == "global"
    ) {
      action = "addGroupCommand";
    }
    let result = await apiQuery("ELA", "patching/patchingManagement", {
      action: action,
      licenseId: props.selectedLicense._id,
      ...addNewCommand.value,
    });
    emit("saved-new-command", result);
    emit("cancel-new-command");
  } catch (error) {
    alerts.value.push({
      type: "error",
      message: "Error saving new command",
      closeable: true,
    });
  }
  loading.value = false;
}

function cancelNewCommand() {
  addNewCommand.value = {};
  emit("cancel-new-command");
}
</script>

<template>
  <AlertsList v-model="alerts" />
  <!-- <v-sheet rounded="lg" height="100%" class="pa-5 mt-5"> -->
  <v-row v-if="!props.hideTitle">
    <v-col cols="12">
      <h2>Add New Command</h2>
    </v-col>
  </v-row>
  <template v-if="!addNewCommand.value.appId">
    <v-row>
      <v-col>
        <v-text-field
          density="compact"
          hide-details
          prepend-inner-icon="mdi-magnify"
          v-model="appSearch"
          placeholder="Search Apps..."
          variant="outlined"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="addAppHeaders"
          :items="cleanAppsList()"
          :loading="props.loading"
          @click:row="selectApp"
          hover
          :search="appSearch"
        >
          <template v-slot:[`item.appId`]="{ item }">
            <v-row align="center">
              <v-col cols="auto">
                <AppIcon :appID="item.appId" />
              </v-col>
              <v-col cols="auto">{{ item.appId }}</v-col>
            </v-row>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-btn class="ml-3" @click="cancelNewCommand">Cancel</v-btn>
      </v-col>
    </v-row>
  </template>
  <template v-else>
    <v-row>
      <v-col>
        <v-text-field
          v-model="addNewCommand.value.name"
          label="Selected App"
          readonly
          variant="underlined"
        ></v-text-field>
        <v-text-field
          v-model="addNewCommand.value.version"
          label="Version to Update to"
          readonly
          variant="underlined"
        ></v-text-field>
        <v-radio-group
          label="Scope of Command"
          v-model="addNewCommand.value.scope"
        >
          <v-radio label="Global" value="global"></v-radio>
          <v-radio label="Configuration Group" value="configuration"></v-radio>
          <v-radio label="Individual Device" value="device"></v-radio>
        </v-radio-group>
        <v-checkbox
          v-if="addNewCommand.value.scope === 'global'"
          label="Include devices assigned a configuration group"
          v-model="addNewCommand.value.applyToAllDevices"
          density="compact"
          hide-details
        ></v-checkbox>
        <template v-if="addNewCommand.value.scope === 'configuration'">
          <v-select
            v-model="addNewCommand.value.groupID"
            :items="props.selectedLicense.Configurations"
            item-title="ConfigurationName"
            item-value="ConfigurationID"
            label="Select Configuration Group"
            variant="underlined"
          ></v-select>
        </template>
        <template v-if="addNewCommand.value.scope === 'device'">
          <template v-if="!addNewCommand.value.serial">
            <v-text-field
              v-model="commandSearchDevice"
              label="Search Device..."
              variant="underlined"
            ></v-text-field>
            <v-data-table
              :headers="chooseDeviceHeaders"
              :items="props.devices"
              :loading="props.loading"
              @click:row="addNewSelectDevice"
              hover
              :search="commandSearchDevice"
            ></v-data-table>
          </template>
          <v-text-field
            v-else
            v-model="addNewCommand.value.serial"
            label="Selected Device Serial"
            readonly
            append-inner-icon="mdi-close"
            @click:append-inner="addNewCommand.value.serial = ''"
            variant="underlined"
          ></v-text-field>
        </template>
        <v-select
          :items="installTypes"
          label="Command Type"
          v-model="addNewCommand.value.type"
          variant="underlined"
        ></v-select>
        <v-row class="mb-3">
          <v-col cols="auto">
            <v-btn
              :disabled="addNewCommandSaveDisabled"
              :loading="loading"
              @click="saveNewCommand"
              color="primary"
              >Save</v-btn
            >
          </v-col>
          <v-col cols="auto">
            <v-btn @click="cancelNewCommand">Cancel</v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </template>
  <!-- </v-sheet> -->
</template>
